<template>
  <TransitionRoot as="template" :show="checkoutStore.isCartOpen">
    <Dialog
      ref="dialog"
      as="div"
      class="relative z-[999]"
      @close="closeWithDelay"
    >
      <div
        class="fixed inset-0 top-[calc(var(--header-height)+var(--discount-compaign-visible-height))] overflow-hidden"
      >
        <div
          class="absolute inset-0 top-[calc(var(--header-height)+var(--discount-compaign-visible-height))] overflow-hidden"
        >
          <div
            class="pointer-events-none fixed bottom-0 right-0 top-[calc(var(--header-height)+var(--discount-compaign-visible-height))] flex max-w-full"
          >
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-250"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-250"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
              @after-enter="onAfterEnter"
            >
              <DialogPanel
                class="pointer-events-auto relative w-screen md:max-w-[494px]"
                :class="{
                  'md:max-w-[694px]': hasRelatedProducts,
                }"
              >
                <div class="flex h-full">
                  <CartRelatedProducts
                    v-if="hasRelatedProducts"
                    variant="drawer-desktop"
                    :should-render="shouldRenderSwiper"
                    :products="relatedProducts"
                    :title="relatedProductsTitle"
                  />
                  <div
                    class="relative flex h-full flex-grow flex-col overflow-hidden bg-white shadow-xl"
                  >
                  <div
                    class="b order-b-gray-60 flex items-center justify-between border-b px-4 py-6"
                  >
                    <DialogTitle class="ui-text-m uppercase text-gray-90">
                      {{ $t("yourShoppingBag") }}
                      {{ cartItemCount }}
                    </DialogTitle>
                    <UiButton
                      variant-type="icon"
                      variant="clear"
                      size="xs"
                      :aria-label="
                        $t('ariaLabelClose', { title: $t('yourShoppingBag') })
                      "
                      @click="checkoutStore.isCartOpen = false"
                    >
                      <UiIcon name="close" class="size-6 text-primary" />
                    </UiButton>
                  </div>
                  <template v-if="cartInfo">
                    <div
                      class="styled-scrollbars relative flex flex-1 flex-col overflow-auto"
                    >
                      <template v-if="!isEmpty(cartItemsGrouped)">
                        <div class="px-4">
                          <LazyCartProductCard
                            v-for="(product, index) in cartItemsGrouped"
                            :key="product.id"
                            :product="product"
                            :product-index="index"
                          />
                        </div>
                        <CartRelatedProducts
                          variant="drawer-mobile"
                          :should-render="shouldRenderSwiper"
                          :products="relatedProducts"
                          :title="relatedProductsTitle"
                        />
                      </template>
                      <div
                        v-else
                        class="pt-6 text-center text-xl text-gray-elements"
                      >
                        {{ $t("cartEmpty") }}
                      </div>
                    </div>
                    <div v-if="!isEmpty(cartItemsGrouped)">
                      <div
                        class="space-y-3.5 border-y border-y-off-white-30 px-4 py-3.5"
                      >
                        <div
                          v-for="info in cartInformation"
                          :key="info.label"
                          class="flex items-center justify-between text-sm font-light"
                        >
                          <div>{{ info.label }}</div>

                          <div :class="cn(info.extraClass)">
                            {{ info.value }}
                          </div>
                        </div>
                      </div>
                      <div class="px-4 py-7.5">
                        <UiButton
                          color="primary"
                          size="lg"
                          :to="
                            localePath(
                              `/checkout/${generalStore.cart?.unique_id || ''}`,
                            )
                          "
                          class="w-full uppercase"
                          @click="handleCheckoutClick"
                          data-e2e-cta="checkout"
                        >
                          {{ $t("checkout") }}
                        </UiButton>
                      </div>
                    </div>
                  </template>
                </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { onClickOutside } from "@vueuse/core";
import { Cart } from "ecom-types";
import CartRelatedProducts from "./CartRelatedProducts.vue";

const checkoutStore = useCheckoutStore();
const generalStore = useGeneralStore();
const generalInternalStore = useGeneralInternalStore();
const {
  closeCart,
  cartItemsGrouped,
  cartInfo,
  cartItemsQuantity,
  cartItems,
  hasOnlyDigitalItemsInCart,
  shippingRate,
  cartUniqueId,
} = useCart();
const { t, n } = useI18n();
const { $api } = useNuxtApp();

const dialog = ref(null);
const shouldRenderSwiper = ref<boolean>(false);
const relatedProductsFullList = ref<any[]>([]);

const productSkusInCart = computed(() => {
  return cartItems.value.map((item) => item.configuration_id);
});

const relatedProducts = computed(() => {
  // Hide related products when cart is empty
  if (isEmpty(cartItemsGrouped.value)) {
    return [];
  }

  return relatedProductsFullList.value.filter((item: any) => {
    const productSKU =
      item?.variant_plp_image?.sku ||
      item?.default_config?.configuration_id ||
      item?.default_sku;

    return !productSkusInCart.value.includes(productSKU);
  });
});

const relatedProductsTitle = computed(() => t("youMightAlsoLike"));

// Keep drawer wide if we have related products loaded (even if filtered out)
// This prevents drawer size changes when removing items
const hasRelatedProducts = computed(() => relatedProductsFullList.value.length > 0);

async function loadRelatedProducts() {
  if (!cartUniqueId.value) {
    return;
  }

  try {
    const { data } = await $api.cart.getCartRelatedProducts(cartUniqueId.value);
    relatedProductsFullList.value = (data as any[]) || [];
  } catch (error) {
    relatedProductsFullList.value = [];
  }
}

const isMounted = useMounted();
const { viewCartEvent } = useEvents();

onClickOutside(dialog, () => {
  closeWithDelay();
});

watch(
  () => checkoutStore.isCartOpen,
  async (val) => {
    generalInternalStore.systemOverlay = val;

    if (val) {
      await loadRelatedProducts();
    } else {
      shouldRenderSwiper.value = false;
      // Reset related products when drawer closes to allow fresh load next time
      relatedProductsFullList.value = [];
    }
  },
);

// Reload related products when cart items change
watch(
  () => cartItems.value.length,
  async (newLength, oldLength) => {
    // Only reload if cart drawer is open and items were removed
    if (checkoutStore.isCartOpen && newLength < oldLength) {
      await loadRelatedProducts();
    }
  },
);

watchEffect(() => {
  if (isMounted.value && checkoutStore.isCartOpen) {
    window.scrollTo(0, 0);
    viewCartEvent(cartInfo.value);
  }
});

const localePath = useLocalePathPolyfill();

const cartItemCount = computed(() => {
  return cartItemsQuantity.value ? `(${cartItemsQuantity.value})` : "";
});

async function onAfterEnter() {
  setTimeout(() => {
    const inertEl = document.querySelector("[inert]");
    if (inertEl) {
      inertEl.removeAttribute("inert");
    }
  }, 0);

  await nextTick();

  shouldRenderSwiper.value = true;
}

const shippingInfo = computed(() => {
  if (shippingRate.value && Number(shippingRate.value.price) > 0) {
    return {
      value: n(Number(shippingRate.value.price), "currency"),
      extraClass: "",
    };
  }
  if (shippingRate.value && Number(shippingRate.value.price) === 0) {
    return {
      value: t("free"),
      extraClass: "text-orange-60 uppercase",
    };
  }
  return {
    value: t("calculatedAtCheckout"),
    extraClass: "text-sand-80",
  };
});

const cartInformation = computed(() => {
  const items = [
    {
      label: t("subtotal"),
      value: n(Number(cartInfo.value?.base_amount), "currency"),
      extraClass: "",
    },
  ];
  if (!hasOnlyDigitalItemsInCart.value) {
    items.push(
      {
        label: t("shipping"),
        value: shippingInfo.value.value,
        extraClass: shippingInfo.value.extraClass,
      },
      {
        label: t("taxes"),
        value: t("calculatedAtCheckout"),
        extraClass: "text-sand-80",
      },
    );
  }
  return items;
});

function closeWithDelay() {
  setTimeout(() => {
    checkoutStore.isCartOpen = false;
  }, 0);
}

function handleCheckoutClick() {
  closeCart();
}
</script>
